/**
 * This notifications store is designed to be a singleton.
 *
 * While it is a class, there's only ever one instance of it.
 */

import uuid from "uuid/v4";
import objectHash from "object-hash";
import { validate } from "objectro";
import { camelCase } from "change-case";
import config from "../config";
import { hasAndIsNotEmpty, findByPropValueFn } from "./helpers";
import debug from "./debug";
import EventEmitter, { Listener, EventCallback } from "./EventEmitter";

export interface EventListeners {
  [eventName: string]: Listener[];
}

export interface SimpleNotification {
  /**
   * The aggregate ID which links multiple notifications to a common subject.
   *
   * This will most likely be the requestId of the CustomerRequest object.
   */
  aggregateId: string | number;
  /** The type of notification. */
  type: string; // NotificationType;
  /**
   * The customer request/response action, i.e. `customerData.created`,
   * `customerData.failed`, etc.
   */
  action: string; // NotificationAction;
  /**
   * The notification can have a specific translation ID, otherwise a
   * default message based on the action will be used.
   */
  translationId?: string;
  /** The sales person ID that the notification is in relation to. */
  salesPersonId: string | number;
  /** Extra parameters to pass to the notification's translated message */
  params?: {
    [paramName: string]: any;
  };
}

export interface Notification extends SimpleNotification {
  /** The notification's ID */
  id: string;
  /** The UNIX timestamp in milliseconds */
  timestamp: number;
  /** Tracks if the notification has been read or not */
  read: boolean;
  /** Hash to check uniqueness of notification (to avoid duplicates) */
  hash: string;
}

export const NOTIFICATIONS_STORAGE_KEY = `${camelCase(config.brand)}_${config
  .notifications.localStorageKey || "notifications"}`;

/**
 * Filter function to remove expired notifications.
 *
 * If timeExpires is 0, then it won't filter any notifications.
 */
export function filterExpiredNotificationsFn(
  timeExpires: number = 0
): (n: Notification) => boolean {
  const minTime = +new Date() - timeExpires;
  return n =>
    !timeExpires || (!!timeExpires && (!n.timestamp || n.timestamp >= minTime));
}

export default class NotificationsStore {
  private localStorageKey: string = NOTIFICATIONS_STORAGE_KEY;
  private emitter: EventEmitter;
  public listeners: EventListeners;
  public store: Notification[];

  constructor(store?: Notification[]) {
    this.emitter = new EventEmitter({
      debug: process.env.NODE_ENV === "development"
    });
    this.listeners = {};
    this.store = [];

    if (store instanceof Array) {
      this.store = [...store];
    } else {
      const localData = localStorage.getItem(this.localStorageKey);
      if (localData) {
        const notifications = JSON.parse(localData).filter(
          filterExpiredNotificationsFn(config.notifications.timeExpires)
        );
        this.store = [...notifications];
      }
    }

    // Clear any older notifications that share the same aggregateId,
    // ensuring there is only one per aggregateId.
    this.clearAggregated();

    debug("[NotificationsStore] initialised", { notificationsStore: this });

    // Persist notifications to the local storage
    this.on("update", () => {
      const updateNotifications = this.store.filter(
        filterExpiredNotificationsFn(config.notifications.timeExpires)
      );

      debug("[NotificationsStore] updated", { updateNotifications });

      localStorage.setItem(
        this.localStorageKey,
        JSON.stringify(updateNotifications)
      );
    });
  }

  /**
   * Get the number of notifications in the store.
   */
  get length(): number {
    return this.store.length;
  }

  /**
   * Get all notifications.
   */
  getAll(): Notification[] {
    return this.store;
  }

  /**
   * Find notification based on property's value
   */
  getByProp(prop: string, value: any): Notification | undefined {
    return this.store.find(findByPropValueFn(prop, value));
  }

  /**
   * Filter notifications based on property's value
   */
  getAllByProp(prop: string, value: any): Notification[] {
    return this.store.filter(findByPropValueFn(prop, value));
  }

  /**
   * Query for a single notification
   */
  getByQuery(query: any): Notification | undefined {
    return this.store.find(n => validate(n, query));
  }

  /**
   * Query for multiple notifications
   */
  getAllByQuery(query: any): Notification[] {
    return this.store.filter(n => validate(n, query));
  }

  /**
   * Retrieve a single notification by ID
   */
  getById(id: any): Notification | undefined {
    return this.getByProp("id", id);
  }

  /**
   * Add a notification to the store
   */
  add(notification: SimpleNotification): Notification | undefined {
    const timestamp = +new Date();
    const hash = objectHash(notification);

    if (this.getByProp("hash", hash)) {
      console.warn("Notification already exists within the store");
      return;
    }

    const _notification = {
      ...notification,
      id: uuid(),
      hash,
      timestamp,
      read: false
    };

    // Clear any older notifications that share the same aggregateId,
    // ensuring the latest notification is at the top.
    this.clearAggregatedById(notification.aggregateId);

    this.store.unshift(_notification);
    this.emit("update");
    return _notification;
  }

  /**
   * Update one or many notifications that match on a prop's name and value.
   */
  updateByProp(
    byPropName: string,
    withPropValue: any,
    updateData: Partial<Notification>
  ): Notification[] | undefined {
    const notifications = this.store.filter(
      findByPropValueFn(byPropName, withPropValue)
    );

    if (notifications.length) {
      const updateNotifications = notifications.map(n => {
        const nIndex = this.store.indexOf(n);
        const _n = { ...n, ...updateData };
        this.store[nIndex] = _n;
        return _n;
      });
      this.store = [...this.store];
      this.emit("update");
      return updateNotifications;
    }
  }

  /**
   * Update one or many notifications that match the query object.
   */
  updateByQuery(
    query: object,
    updateData: Partial<Notification>
  ): Notification[] | undefined {
    const notifications = this.store.filter(n => validate(n, query));

    if (notifications.length) {
      const updateNotifications = notifications.map(n => {
        const nIndex = this.store.indexOf(n);
        const _n = { ...n, ...updateData };
        this.store[nIndex] = _n;
        return _n;
      });
      this.store = [...this.store];
      this.emit("update");
      return updateNotifications;
    }
  }

  /**
   * Subscribe to the notification store's events.
   */
  on(eventName: string, callback: EventCallback): Listener {
    // Bind the NotificationsStore to the callback when subscribing
    const eventListener = this.emitter.subscribe(
      eventName,
      callback.bind(this)
    );

    const removeEventListener = () => {
      this.off(eventName, eventListener);
    };

    const listener = {
      unsubscribe: function removeListener(): void {
        removeEventListener();
      }
    };

    // Add the listener
    if (
      hasAndIsNotEmpty(this.listeners, eventName) &&
      this.listeners[eventName] instanceof Array
    ) {
      this.listeners[eventName].push(eventListener);
    } else {
      this.listeners[eventName] = [eventListener];
    }

    return listener;
  }

  /**
   * Unsubscribe a listener from the store.
   *
   * Private, use the unsubscribe method on the returned listener object.
   */
  private off(eventName: string, listener: Listener) {
    if (hasAndIsNotEmpty(this.listeners, eventName)) {
      // Remove only the given listener
      if (listener) {
        const listenerIndex = this.listeners[eventName].indexOf(listener);
        if (listenerIndex !== -1) {
          this.listeners[eventName]
            .splice(listenerIndex, 1)
            // Unsubscribe from the EventEmitter's listener
            .forEach(l => l.unsubscribe());

          if (!this.listeners[eventName].length) {
            delete this.listeners[eventName];
          }
        }
      }
      // Remove all listeners
      else {
        this.listeners[eventName].forEach(l => l.unsubscribe());
        delete this.listeners[eventName];
      }
    }
  }

  /**
   * Clears any aggregated notifications leaving only the last known.
   *
   * Does not trigger an `updated` event but does mutate the store array.
   */
  private clearAggregated(): void {
    const unique: { [aggregateId: string]: number } = {};
    this.store = this.store.reduce((notifications: Notification[], n) => {
      if (!(n.aggregateId in unique)) {
        unique[n.aggregateId] = 1;
        notifications.push(n);
      }
      return notifications;
    }, []);
  }

  /**
   * Removes any older notifications that share the same aggregateId.
   *
   * Does not trigger an `updated` event but does mutate the store array.
   */
  private clearAggregatedById(aggregateId: string | number): void {
    // Remove any notifications that correspond to the aggregateId
    const aggregatedIndexes = this.getAllByProp("aggregateId", aggregateId).map(
      n => {
        return this.store.indexOf(n);
      }
    );
    if (aggregatedIndexes.length) {
      debug(
        "[NotificationsStore] Found existing notifications which share same aggregateId",
        {
          aggregateId,
          indexes: aggregatedIndexes
        }
      );
      aggregatedIndexes.forEach(index => {
        this.store.splice(index);
      });
    }
  }

  /**
   * Emit a specific event on the store.
   */
  emit(eventName: string, data?: any) {
    this.emitter.publish(eventName, data);
  }

  /**
   * Mark all unread notifications as read.
   */
  markAllRead() {
    const unreadNotifications = this.getAllByProp("read", false);
    if (unreadNotifications && unreadNotifications.length) {
      unreadNotifications.forEach(notification => (notification.read = true));
      this.emit("update");
    }
  }

  /**
   * Mark a single notification as read.
   */
  markReadById(id: any) {
    this.updateByProp("id", id, {
      read: true
    });
  }
}
